import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  DriverData,
  DriverDetails,
} from 'src/app/models/driver/driver.model';
import { GeoPoint } from 'src/app/models/driver/driver.model';
import { UnassignedService } from 'src/app/models/map-drivers/map-drivers.model';
import { OrderData, Ubication } from 'src/app/models/service/service.model';
import { DriverService } from 'src/app/services/driver/driver.service';
import {
  DriverMapPointItem,
  ExternalDriverEntry,
} from 'src/app/services/mapa/interfaces/mapa-data-unifier.interface';
import { MapaServiceService, ServiceFilterStartEndData } from 'src/app/services/mapa/mapa-service.service';

export interface MapPointItem {
  type: 'pickup' | 'dest';
  obj: Ubication;
  data: OrderData;
}

export interface ServiceManagerMapPointItem {
  type: 'pickup' | 'dest';
  obj: GeoPoint;
  data: UnassignedService;
}

interface PoolServicesResponse {
  mapData?: OrderData[];
}

@Injectable({
  providedIn: 'root',
})
export class MapaDataUnifierService {
  constructor(
    private driverService: DriverService,
    private ms: MapaServiceService
  ) {}
  sanitizeDriverId(driverId: string): string {
    if (!driverId) {
      return '';
    }

    const withoutDashes = driverId.replace(/-/g, '');
    if (/^0+$/.test(withoutDashes)) {
      return '';
    }

    return driverId;
  }

  formatServiceDate(date: string): string {
    const isoDate = this.extractIsoDate(date);
    const parsed = this.parseDate(isoDate);
    if (!parsed) {
      return '';
    }

    return `${this.formatDayMonthYear(parsed)} ${this.formatHourMinute(parsed)}`;
  }

  formatServiceDateOnly(date: string, locale: string): string {
    const isoDate = this.extractIsoDate(date);
    const parsed = this.parseDate(isoDate);
    if (!parsed) {
      return '';
    }

    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
      .format(parsed)
      .replace(',', '');
  }

  formatServiceHour(date: string): string {
    const isoDate = this.extractIsoDate(date);
    const parsed = this.parseDate(isoDate);
    if (!parsed) {
      return '';
    }

    return this.formatHourMinute(parsed);
  }

  normalizePoolServices(services: OrderData[], locale: string): OrderData[] {
    return services.map((service: OrderData) => ({
      ...service,
      serviceDateOnly: this.formatServiceDateOnly(service.serviceDate, locale),
      serviceHour: this.formatServiceHour(service.serviceDate),
      driverGuid: this.sanitizeDriverId(service.driverGuid),
    }));
  }

  buildPoolMapPoints(services: OrderData[]): MapPointItem[] {
    const points: MapPointItem[] = [];

    services.forEach((service) => {
      if (this.hasValidPoolUbication(service.startUbication)) {
        points.push({
          type: 'pickup',
          obj: service.startUbication,
          data: service,
        });
      }

      if (this.hasValidPoolUbication(service.endUbication)) {
        points.push({
          type: 'dest',
          obj: service.endUbication,
          data: service,
        });
      }
    });

    return points;
  }

  normalizeServiceManagerService(service: UnassignedService): UnassignedService {
    const serviceHour = this.extractServiceManagerHour(service);
    const pickupAddress =
      service?.pickupAddress ?? service?.startUbication?.address ?? '';
    const destinationAddress =
      service?.destinationAddress ?? service?.endUbication?.address ?? '';

    return {
      ...service,
      FormatedHour: service?.FormatedHour ?? serviceHour,
      mainProductName: service?.mainProductName ?? service?.productName,
      productName: service?.productName ?? service?.mainProductName,
      chassis: service?.chassis ?? service?.vehicleType,
      vehicleType: service?.vehicleType ?? service?.chassis,
      pickupAddress,
      destinationAddress,
      startUbication:
        service?.startUbication ??
        this.geoPointToUbication(service?.pickupPoint, pickupAddress),
      endUbication:
        service?.endUbication ??
        this.geoPointToUbication(service?.destinationPoint, destinationAddress),
    };
  }

  buildServiceManagerMapPoints(services: UnassignedService[]): ServiceManagerMapPointItem[] {
    const points: ServiceManagerMapPointItem[] = [];

    services.forEach((service) => {
      const pickupPoint = service.pickupPoint;
      const destinationPoint = service.destinationPoint;

      if (this.hasValidGeoPoint(pickupPoint)) {
        points.push({
          type: 'pickup',
          obj: pickupPoint,
          data: service,
        });
      }

      if (this.hasValidGeoPoint(destinationPoint)) {
        points.push({
          type: 'dest',
          obj: destinationPoint,
          data: service,
        });
      }
    });

    return points;
  }

  async loadPoolServices(
    serviceData: ServiceFilterStartEndData
  ): Promise<OrderData[]> {
    let assigned: OrderData[] = [];
    let unassigned: OrderData[] = [];

    try {
      const assignedResponse = (await firstValueFrom(
        this.ms.getAssignedServices(serviceData)
      )) as unknown as PoolServicesResponse;
      assigned = assignedResponse?.mapData ?? [];
    } catch (error) {
      console.error('Error al obtener servicios asignados (pool):', error);
    }

    try {
      const unassignedResponse = (await firstValueFrom(
        this.ms.getUnassignedServices(serviceData)
      )) as unknown as PoolServicesResponse;
      unassigned = unassignedResponse?.mapData ?? [];
    } catch (error) {
      console.error('Error al obtener servicios no asignados (pool):', error);
    }

    return [...unassigned, ...assigned];
  }

  /**
   * Carga los conductores autónomos activos desde /drivers/active-external-drivers,
   * enriquece cada uno con su detalle completo y marca si tiene servicio asignado.
   * Devuelve objetos con la misma forma que los drivers de Service Manager:
   * { driverId, general_info: DriverDetails, assignedServices: [], serviceAssigned }
   * para que la plantilla de MapaComponent funcione en ambos modos.
   */
  async loadExternalDrivers(
    elements_services: OrderData[],
    pointsMarkers: Array<ServiceManagerMapPointItem | MapPointItem | DriverMapPointItem>
  ): Promise<ExternalDriverEntry[]> {
    try {
      const res = await firstValueFrom(
        this.driverService.getActiveExternalDrivers()
      );

      const activeDrivers = res?.activeDrivers;
      if (!activeDrivers?.length) {
        return [];
      }

      const driverPromises = activeDrivers.map(async (element: DriverData) => {
        const driverObj = await this.ms
          .getDriver(element.driverGuid)
          .toPromise();

        if (!driverObj?.drivers?.length) return null;

        const driver: DriverDetails = driverObj.drivers[0];
        if (!driver) return null;

        const serviceAssigned = elements_services.some(
          (s) => this.sanitizeDriverId(s.driverGuid) === driver.driverId
        );

        if (driver.lastLocation) {
          pointsMarkers.push({
            type: 'driver',
            obj: driver.lastLocation,
            data: driver,
          });
        }

        return {
          driverId: driver.driverId,
          general_info: { ...driver, serviceAssigned },
          assignedServices: [],
          serviceAssigned,
        };
      });

      const results = await Promise.allSettled(driverPromises);
      const rejectedCount = results.filter(
        (r) => r.status === 'rejected'
      ).length;
      if (rejectedCount > 0) {
        console.warn(
          `Fallaron ${rejectedCount} llamadas de detalle de conductor autónomo, se continúa con los datos disponibles.`
        );
      }

      const drivers = results
        .map((result) => (result.status === 'fulfilled' ? result.value : null))
        .filter((d): d is ExternalDriverEntry => d !== null);

      return drivers;
    } catch (error) {
      console.error('Error al cargar conductores autónomos:', error);
      return [];
    }
  }

  private extractIsoDate(date: string): string {
    return (date || '').split(' ')[0];
  }

  private parseDate(value: string): Date | null {
    if (!value) {
      return null;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return parsed;
  }

  private formatDayMonthYear(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private formatHourMinute(date: Date): string {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private extractServiceManagerHour(service: UnassignedService): string {
    const pickupTime = service?.pickupDate?.time;
    if (!pickupTime || typeof pickupTime !== 'string') {
      return '';
    }

    const hourParts = pickupTime.split(':');
    if (hourParts.length < 2) {
      return '';
    }

    return `${hourParts[0]}:${hourParts[1]}`;
  }

  private geoPointToUbication(
    point: GeoPoint | undefined | null,
    address: string
  ): Ubication | undefined {
    if (!this.hasValidGeoPoint(point)) {
      return undefined;
    }

    return {
      latitude: point.Latitude,
      longitude: point.Longitude,
      address,
    };
  }

  private hasValidGeoPoint(point: unknown): point is GeoPoint {
    const geoPoint = point as Partial<GeoPoint> | null;
    return (
      !!geoPoint &&
      typeof geoPoint.Latitude === 'number' &&
      typeof geoPoint.Longitude === 'number'
    );
  }

  private hasValidPoolUbication(ubication: Ubication | undefined): ubication is Ubication {
    return !!ubication &&
      typeof ubication.latitude === 'number' &&
      typeof ubication.longitude === 'number';
  }
}
